import * as React from "react";
import { cn } from "@/lib/cn";
import { PLACEHOLDER } from "@/lib/placeholders";
import { InfoHint } from "./InfoHint";

/**
 * Labelled form field + the input primitives that go inside it. Appearance is
 * `.field` / `.input` / `.password` in `styles/ui.css`.
 *
 * There is no `Select` here. There was, wrapping a native `<select>`, and its
 * last call site went when the Team dialogs moved to `FilterSelect
 * variant="field"` - same 40px, plus a search box past six options and a second
 * line per option, neither of which a native select can do. Do not add it back
 * for a form: two controls that both mean "pick one of these" is how a form ends
 * up with one of each in adjacent rows.
 *
 * Two decisions worth keeping:
 *
 * 1. **The label sits above the input, never floating inside it.** A floating
 *    label is unreadable at arm's length on a tablet in sunlight, and it
 *    disappears exactly when the user most wants to check what they are typing.
 *
 * 2. **Error and hint text occupy reserved space.** The message slot always
 *    renders, so a failed submit does not push the submit button down under the
 *    user's finger between the press and the release.
 */

/**
 * A stack of `Field`s.
 *
 * **`noValidate` is the default, and that is the whole reason this exists.**
 * Every form in this product renders its own errors through `Field`. Leave the
 * browser validation on and you get a second, competing error system: a native
 * bubble that appears somewhere else, is styled by the OS, cannot be translated,
 * and blocks submit before our handler ever runs. It was spelled out at all nine
 * forms; the tenth is the one that would have shipped without it.
 *
 * `gap` is a prop rather than something passed through `className`, because both
 * would be gap rules on one element and the later one in the stylesheet wins
 * regardless of what the author wrote.
 */
export function Form({
  onSubmit,
  id,
  gap = "tight",
  className,
  children,
}: {
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  /** Needed when the submit button lives outside the form, e.g. a `Modal` footer. */
  id?: string;
  gap?: "tight" | "loose";
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <form
      id={id}
      onSubmit={onSubmit}
      noValidate
      className={cn("form", gap === "loose" && "form--loose", className)}
    >
      {children}
    </form>
  );
}

/**
 * A titled group of fields inside a form.
 *
 * The same shape as `DrawerSection` and deliberately not that component: a
 * drawer's sections are hairline-separated full-bleed bands sized to a 28rem
 * panel, and reusing them in a modal meant importing `Drawer` for its styling
 * into forms that no longer open one.
 *
 * `description` is for a rule the reader cannot infer from the fields - why a
 * checkbox grants nothing, what a value is used for downstream. Not for
 * restating the title.
 */
export function FormSection({
  title,
  description,
  info,
  children,
}: {
  title: string;
  description?: React.ReactNode;
  /** The same kind of rule as `description`, behind an info mark beside the title. */
  info?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="form-section">
      {info ? (
        <div className="info-hint-row">
          <h3 className="form-section__title">{title}</h3>
          <InfoHint text={info} label={`About ${title}`} />
        </div>
      ) : (
        <h3 className="form-section__title">{title}</h3>
      )}
      {description && <p className="form-section__description">{description}</p>}
      <div className="form-section__body">{children}</div>
    </section>
  );
}

export interface FieldProps {
  label: React.ReactNode;
  /** Marks the field required and renders the asterisk. */
  required?: boolean;
  /**
   * A control on the label's own row, right-aligned. For "Forgot your
   * password?" and similar.
   *
   * A row of its own is the obvious alternative and it costs 30px of vertical
   * space on the one screen in this product that must not scroll. It goes
   * *beside* the `<label>` rather than inside it, because a link inside a label
   * steals the click that was meant to focus the field.
   */
  labelAction?: React.ReactNode;
  error?: string;
  hint?: string;
  /**
   * What the field is for, behind an info mark beside the label rather than in
   * the message slot under it. For a form dense enough that a line of help under
   * every control is the clutter. Beside the `<label>`, not inside it: the mark
   * is a button, and a button inside a label is invalid.
   */
  info?: string;
  /** Ties label/error to the control. Generated when omitted. */
  htmlFor?: string;
  children: React.ReactNode;
  className?: string;
}

export function Field({
  label,
  required,
  labelAction,
  error,
  hint,
  info,
  htmlFor,
  children,
  className,
}: FieldProps) {
  const message = error ?? hint;

  const labelElement = (
    <label htmlFor={htmlFor} className="field__label">
      {label}
      {required && (
        <span className="field__required" aria-hidden>
          *
        </span>
      )}
    </label>
  );

  return (
    <div className={cn("field", className)}>
      <div className="field__label-row">
        {info ? (
          <span className="info-hint-row">
            {labelElement}
            <InfoHint text={info} label="About this field" />
          </span>
        ) : (
          labelElement
        )}
        {labelAction}
      </div>

      {children}

      <p
        className={cn("field__message", error && "field__message--error")}
        role={error ? "alert" : undefined}
      >
        {message}
      </p>
    </div>
  );
}

/**
 * Three heights, and the choice is about who is touching the screen.
 *
 * `md` is 40px and the default: the console and the auth screens, both of which
 * are a mouse and a keyboard at a desk. `sm` is 36px for a dense row of controls
 * such as a filter bar.
 *
 * `touch` is 56px, the glove-operable floor from PRD §11, with enough vertical
 * room that the text stays clear of the border at a 1.4× OS text scale. It is
 * for a screen a technician uses outdoors, and it used to be the default here -
 * which is how a sign-up form ended up too tall for a laptop viewport.
 */
export type InputSize = "sm" | "md" | "touch";

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "className"> {
  invalid?: boolean;
  inputSize?: InputSize;
  className?: string;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(function Input(
  { invalid, inputSize = "md", className, ...rest },
  ref
) {
  return (
    <input
      ref={ref}
      aria-invalid={invalid || undefined}
      className={cn("input", `input--${inputSize}`, invalid && "input--invalid", className)}
      {...rest}
    />
  );
});

/** Password input with a reveal toggle sized for a gloved thumb. */
export const PasswordInput = React.forwardRef<HTMLInputElement, InputProps>(function PasswordInput(
  { className, placeholder = PLACEHOLDER.password, ...rest },
  ref
) {
  const [visible, setVisible] = React.useState(false);

  return (
    <div className="password">
      <Input
        ref={ref}
        type={visible ? "text" : "password"}
        className={cn("password__input", className)}
        placeholder={placeholder}
        {...rest}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        // The state is announced rather than drawn, so the control needs no
        // icon swap to be understood by a screen reader.
        aria-label={visible ? "Hide password" : "Show password"}
        aria-pressed={visible}
        className="password__toggle"
      >
        {visible ? "Hide" : "Show"}
      </button>
    </div>
  );
});
